import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, FormControl, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material";
import { CiSearch } from "react-icons/ci";
import { XMarkIcon } from "@heroicons/react/20/solid";
import InviteMemberModal from "./components/organisation/InviteMemberModal";
import RolePickerModal from "./components/organisation/RolePickerModal";
import usePlanGate from "./hooks/usePlanGate";
import {
  ConfirmDialog,
  ErrorBox,
  Notice,
  OrgModal,
  OrgPageHeader,
  Pager,
  PrimaryButton,
  RoleBadge,
  Spinner,
  StatusBadge,
} from "./components/organisation/OrgModal";
import { handleLogout } from "./components/auth/authHelpers";
import {
  approveJoinRequest,
  assignableRoles,
  cachedActiveOrganisation,
  can,
  canManageMember,
  changeMemberRole,
  findNextOrganisation,
  fetchMembers,
  formatOrgDate,
  MEMBER_STATUS,
  MEMBER_TYPE,
  ORG_ROLE,
  orgErrorMessage,
  readSession,
  rejectJoinRequest,
  removeMember,
  resendInvite,
  revokeInvite,
  roleLabel,
  startSession,
  switchOrganisation,
} from "./utils/organisationApi";

const TABS = {
  MEMBERS: "members",
  INVITES: "invites",
  REQUESTS: "requests",
};

// ?active=join_request opens the Join requests tab. "joinrequest" was written by older links, so accept both.
const JOIN_REQUEST_PARAM = "join_request";
const isJoinRequestParam = (value) => ["join_request", "joinrequest"].includes(String(value || "").trim().toLowerCase());

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: ORG_ROLE.OWNER, label: "Owner" },
  { value: ORG_ROLE.ADMIN, label: "Admin" },
  { value: ORG_ROLE.HR, label: "HR" },
  { value: ORG_ROLE.PANELIST, label: "Panelist" },
];

// Join request notes longer than this are cut to one line with "View more"
const NOTE_PREVIEW_LENGTH = 30;
const isLongNote = (message = "") => message.length > NOTE_PREVIEW_LENGTH || message.includes("\n");

const rowKey = (row) => row.userId || row.inviteId || row.email;

const isApprovedJoinRequest = (row) => row.type === MEMBER_TYPE.JOIN_REQUEST && Boolean(row.approvedBy);

const rowWhen = (row) => {
  if (row.type === MEMBER_TYPE.MEMBER) return formatOrgDate(row.joinedAt);
  return formatOrgDate(row.createdAt);
};

const OrganisationMembersPage = () => {
  const theme = useTheme();
  const session = useMemo(() => readSession(), []);
  const organisationName = cachedActiveOrganisation()?.name || "";
  const roles = assignableRoles(session);
  // Panelists are invited by adding them on the Panelists page
  // Panelists are added from the Panelists page, and Admins are not invited from here
  const inviteRoles = roles.filter((role) => role !== ORG_ROLE.PANELIST && role !== ORG_ROLE.ADMIN);
  const canApprove = can(session, "approveJoinRequests");
  // Inviting an Admin or HR person uses a recruiter seat (panelists are added from the Panelists page)
  const inviteGate = usePlanGate("recruiter_seats");

  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [tab, setTab] = useState(() =>
    isJoinRequestParam(searchParams.get("active")) && canApprove ? TABS.REQUESTS : TABS.MEMBERS
  );
  const [roleFilter, setRoleFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notice, setNotice] = useState("");

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  // Join request whose full note is open
  const [noteTarget, setNoteTarget] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  const loadRows = async () => {
    setIsLoading(true);
    setListError("");
    try {
      const result = await fetchMembers({ role: roleFilter });
      setRows(result.members);
    } catch (err) {
      setRows([]);
      setListError(orgErrorMessage(err, "Could not load the team."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const wantsRequests = isJoinRequestParam(searchParams.get("active")) && canApprove;
    if (wantsRequests) setTab(TABS.REQUESTS);
    else setTab((current) => (current === TABS.REQUESTS ? TABS.MEMBERS : current));
  }, [searchParams, canApprove]);

  const selectTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === TABS.REQUESTS) next.set("active", JOIN_REQUEST_PARAM);
    else next.delete("active");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [tab, searchQuery, roleFilter]);

  const visibleRows = rows.filter((row) => {
    if (tab === TABS.MEMBERS && row.type !== MEMBER_TYPE.MEMBER) return false;
    if (tab === TABS.INVITES && row.type !== MEMBER_TYPE.INVITE) return false;
    if (tab === TABS.REQUESTS && row.type !== MEMBER_TYPE.JOIN_REQUEST) return false;
    const search = searchQuery.trim().toLowerCase();
    if (!search) return true;
    return `${row.name} ${row.email}`.toLowerCase().includes(search);
  });
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / rowsPerPage));
  const page = Math.min(currentPage, totalPages);
  const pagedRows = visibleRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const memberCount = rows.filter((row) => row.type === MEMBER_TYPE.MEMBER).length;
  const inviteCount = rows.filter((row) => row.type === MEMBER_TYPE.INVITE).length;
  const requestCount = rows.filter((row) => row.type === MEMBER_TYPE.JOIN_REQUEST).length;
  const tabs = [
    { id: TABS.MEMBERS, label: "Members", count: memberCount },
    { id: TABS.INVITES, label: "Pending invites", count: inviteCount },
    ...(canApprove ? [{ id: TABS.REQUESTS, label: "Join requests", count: requestCount }] : []),
  ];
  const emptyCopy = {
    [TABS.MEMBERS]: ["No members", searchQuery || roleFilter ? "Try a different search or role." : "Invite your team to get started."],
    [TABS.INVITES]: ["No pending invites", "Invites you send will show here until they're accepted."],
    [TABS.REQUESTS]: ["No join requests", "When someone from your company domain signs up, their request shows here."],
  }[tab];

  const openConfirm = (config) => {
    setConfirmError("");
    setConfirm(config);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError("");
    try {
      await confirm.action();
      if (!confirm.leaves) {
        setNotice(confirm.success);
        setConfirm(null);
        setConfirmBusy(false);
        loadRows();
      }
    } catch (err) {
      setConfirmError(orgErrorMessage(err, confirm.fallbackError));
      setConfirmBusy(false);
    }
  };

  // next was found before leaving, while the token still worked
  const afterLeaving = async (next) => {
    if (next) {
      try {
        const token = await switchOrganisation(next.id);
        if (token) {
          startSession(token, "/");
          return;
        }
      } catch {
        // Sign out below if we cannot land in the other organisation
      }
    }
    handleLogout();
  };

  const confirmRemove = (member) =>
    openConfirm({
      title: "Remove Member",
      message: (
        <p>
          Remove <span className="font-bold">{member.name || member.email}</span> from {organisationName || "this organisation"}?
          Their access ends immediately. Jobs, candidates and feedback they created stay with the organisation.
        </p>
      ),
      confirmLabel: "Remove",
      busyLabel: "Removing...",
      success: `${member.name || member.email} was removed.`,
      fallbackError: "Could not remove this member.",
      action: () => removeMember(member.userId),
    });

  const confirmLeave = () =>
    openConfirm({
      title: "Leave Organisation",
      leaves: true,
      message: (
        <p>
          Leave {organisationName || "this organisation"}? Your access ends immediately. Jobs, candidates and feedback you
          created stay with the organisation.
        </p>
      ),
      confirmLabel: "Leave",
      busyLabel: "Leaving...",
      fallbackError: "Could not leave this organisation.",
      action: async () => {
        const next = await findNextOrganisation(session.organisationId);
        await removeMember(session.actorId);
        await afterLeaving(next);
      },
    });

  const confirmRevoke = (invite) =>
    openConfirm({
      title: "Revoke Invite",
      message: (
        <p>
          Revoke the invite to <span className="font-bold">{invite.email}</span>? The link in their email will stop working.
        </p>
      ),
      confirmLabel: "Revoke Invite",
      busyLabel: "Revoking...",
      success: `Invite to ${invite.email} was revoked.`,
      fallbackError: "Could not revoke the invite.",
      action: () => revokeInvite(invite.inviteId),
    });

  const confirmReject = (request) =>
    openConfirm({
      title: "Decline Request",
      message: (
        <p>
          Decline <span className="font-bold">{request.name || request.email}</span>'s request to join? They won't get access.
        </p>
      ),
      confirmLabel: "Decline",
      busyLabel: "Declining...",
      success: `Request from ${request.email} was declined.`,
      fallbackError: "Could not decline the request.",
      action: () => rejectJoinRequest(request.inviteId),
    });

  const handleResend = async (invite) => {
    try {
      await resendInvite(invite.inviteId);
      setNotice(`Invite sent again to ${invite.email}.`);
      loadRows();
    } catch (err) {
      setListError(orgErrorMessage(err, "Could not resend the invite."));
    }
  };

  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
    paddingLeft: 2,
  };
  const cellSx = { paddingLeft: 2, paddingRight: 2 };
  const actionSx = (fill, hoverFill) => ({
    textTransform: "none",
    minWidth: "max-content",
    height: "26px",
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.2,
    px: 1.25,
    py: 0.25,
    borderRadius: "4px",
    backgroundColor: fill,
    color: "#ffffff",
    "&:hover": { backgroundColor: hoverFill },
    "&:focus-visible": { outline: "2px solid #000000", outlineOffset: "2px" },
    "&.Mui-disabled": {
      backgroundColor: fill,
      color: "#ffffff",
      opacity: 0.5,
    },
  });
  const primaryActionSx = actionSx(theme.palette.info.main, "#1D4690");
  const dangerActionSx = actionSx("#b40909", "#8f0707");

  const memberActions = (row, disabled = false) => (
    <div className="flex items-center gap-1.5">
      {roles.length > 1 && (
        <Button
          size="small"
          sx={primaryActionSx}
          disabled={disabled}
          onClick={() => setRoleTarget(row)}
          aria-label={`Change role for ${row.name || row.email}`}
        >
          Change role
        </Button>
      )}
      <Button
        size="small"
        sx={dangerActionSx}
        disabled={disabled}
        onClick={() => confirmRemove(row)}
        aria-label={`Remove ${row.name || row.email}`}
      >
        Remove
      </Button>
    </div>
  );

  const inviteActions = (row, disabled = false) => (
    <div className="flex items-center gap-1.5">
      <Button size="small" sx={primaryActionSx} disabled={disabled} onClick={() => handleResend(row)} aria-label={`Resend invite to ${row.email}`}>
        Resend
      </Button>
      <Button
        size="small"
        sx={dangerActionSx}
        // Only a pending invite can be revoked
        disabled={disabled || row.status !== MEMBER_STATUS.PENDING}
        onClick={() => confirmRevoke(row)}
        aria-label={`Revoke invite to ${row.email}`}
      >
        Revoke
      </Button>
    </div>
  );

  const requestActions = (row, disabled = false) => (
    <div className="flex items-center gap-1.5">
      <Button size="small" sx={primaryActionSx} disabled={disabled} onClick={() => setApproveTarget(row)} aria-label={`Approve ${row.email}`}>
        Approve
      </Button>
      <Button size="small" sx={dangerActionSx} disabled={disabled} onClick={() => confirmReject(row)} aria-label={`Decline ${row.email}`}>
        Decline
      </Button>
    </div>
  );

  // Every row shows its actions; ones this person can't use are greyed out instead of hidden
  const renderActions = (row) => {
    if (row.type === MEMBER_TYPE.INVITE) {
      return inviteActions(row, !canManageMember(session, row.role));
    }

    if (isApprovedJoinRequest(row)) {
      return <span className="text-sm font-medium text-accent-gray">Approved</span>;
    }

    if (row.type === MEMBER_TYPE.JOIN_REQUEST) {
      return requestActions(row, !canApprove);
    }

    // Members, including ones no longer active (e.g. removed by a Super Admin)
    const isActive = row.status === MEMBER_STATUS.ACTIVE;
    const isYou = row.userId === session.actorId;
    const isOwner = row.role === ORG_ROLE.OWNER;
    if (isActive && isYou) {
      return (
        <div className="flex items-center gap-1.5">
          {roles.length > 1 && (
            <Button
              size="small"
              sx={primaryActionSx}
              disabled={true}
              aria-label={`Change role for ${row.name || row.email}`}
            >
              Change role
            </Button>
          )}
          <Button size="small" sx={dangerActionSx} onClick={confirmLeave} disabled={isOwner} aria-label="Leave organisation">
            Leave
          </Button>
        </div>
      );
    }
    return memberActions(row, !isActive || isOwner || !canManageMember(session, row.role));
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <div className="flex flex-col md:justify-between lg:flex-row items-start lg:items-center gap-4 px-6 max-md:px-4 pt-4">
        <div className="relative w-full sm:w-64">
          <CiSearch
            style={{
              position: "absolute",
              left: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              color: theme.palette.text.main,
              pointerEvents: "none",
              fontSize: "16px",
            }}
          />
          <input
            type="text"
            placeholder="Search name or email"
            aria-label="Search name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? "pr-8" : ""}`}
            style={{ borderColor: theme.palette.secondary.main, color: theme.palette.text.main }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
              aria-label="Clear search"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="select-container gap-3 flex flex-wrap items-center">
        <FormControl
          size="small"
          sx={{
            minWidth: 130,
            backgroundColor: theme.palette.primary.main,
            borderRadius: "4px",
            border: `1px solid ${theme.palette.secondary.main}`,
            "& .MuiInputBase-root": {
              height: "30px",
              paddingTop: "4px",
              paddingBottom: "4px",
              paddingLeft: "10px",
              paddingRight: "10px",
              boxSizing: "border-box",
              color: theme.palette.text.main,
              [theme.breakpoints.down("md")]: {
                fontSize: "14px",
              },
            },
            "& .MuiSelect-select": { padding: 0, display: "flex", alignItems: "center" },
            "& .MuiSelect-icon": { color: theme.palette.text.main },
            "& fieldset": { border: "none" },
          }}
        >
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            displayEmpty
            size="small"
            inputProps={{ "aria-label": "Filter by role" }}
          >
            {ROLE_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all-roles"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {inviteRoles.length > 0 && (
          <>
            <PrimaryButton {...inviteGate.controlProps} onClick={inviteGate.guard(() => setIsInviteOpen(true))}>
              Invite Member
            </PrimaryButton>
            {inviteGate.hint}
          </>
        )}
      </div></div>
      <div className="px-6 max-md:px-4 pt-4">
        {/* Below 500px the tabs collapse into a select */}
        <div className="hidden max-[768px]:block">
        <FormControl
          size="small"
          fullWidth
          sx={{
            backgroundColor: theme.palette.primary.main,
            borderRadius: "4px",
            border: `1px solid ${theme.palette.secondary.main}`,
            "& .MuiInputBase-root": {
              height: "36px",
              paddingLeft: "10px",
              paddingRight: "10px",
              boxSizing: "border-box",
              color: theme.palette.text.main,
              fontSize: "14px",
            },
            "& .MuiSelect-select": { padding: 0, display: "flex", alignItems: "center" },
            "& .MuiSelect-icon": { color: theme.palette.text.main },
            "& fieldset": { border: "none" },
          }}
        >
          <Select
            value={tab}
            onChange={(e) => selectTab(e.target.value)}
            size="small"
            inputProps={{ "aria-label": "Team sections" }}
          >
            {tabs.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label} ({item.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        </div>
        <div
          role="tablist"
          aria-label="Team sections"
          className="inline-flex max-[768px]:hidden max-w-full flex-wrap gap-1 border"
          style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
        >
          {tabs.map((item) => {
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectTab(item.id)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black ${active ? "" : "hover:bg-black/[0.04]"}`}
                style={{
                  backgroundColor: active ? theme.palette.info.main : "transparent",
                  color: active ? "#ffffff" : theme.palette.text.main,
                }}
              >
                {item.label}
                <span
                  className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold"
                  style={{
                    backgroundColor: active ? "rgba(255, 255, 255, 0.22)" : theme.palette.secondary.v2,
                    color: active ? "#ffffff" : theme.palette.info.main,
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Notice onDismiss={() => setNotice("")}>{notice}</Notice>

      {isLoading ? (
        <Spinner label="Loading team..." />
      ) : (
        <div
          className="min-h-0 overflow-auto border rounded-md mx-6 max-md:mx-4 mt-4"
          style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
        >
          {listError && (
            <div className="m-4">
              <ErrorBox>{listError}</ErrorBox>
            </div>
          )}
          {visibleRows.length === 0 ? (
            listError ? null : (
              <div className="flex flex-col items-center justify-center h-80 text-accent-gray">
                <p className="text-lg font-medium mb-1">{emptyCopy[0]}</p>
                <p className="text-sm max-w-md text-center px-6">{emptyCopy[1]}</p>
              </div>
            )
          ) : (
            <Table
              stickyHeader
              className="w-full"
              sx={{ backgroundColor: theme.palette.primary.main, tableLayout: "fixed", minWidth: 1050 }}
            >
              {/* Fixed widths keep every column in the same place on Members, Pending invites and Join requests */}
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 200 }}>Name</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 280 }}>Email</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>Status</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 120 }}>Role</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 130 }}>Date</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 200 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => {
                  const isYou = row.type === MEMBER_TYPE.MEMBER && row.userId === session.actorId;
                  return (
                    <TableRow key={rowKey(row)}>
                      <TableCell sx={{ ...cellSx, maxWidth: 240, overflowWrap: "anywhere" }}>
                        {row.name || "—"}
                        {row.message && (
                          <span className="block text-sm text-accent-gray-light mt-0.5">
                            <span className="line-clamp-1">{row.message}</span>
                            {isLongNote(row.message) && (
                              <button
                                type="button"
                                onClick={() => setNoteTarget(row)}
                                className="mt-0.5 text-sm font-medium underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                                style={{ color: theme.palette.info.main }}
                                aria-label={`View the full note from ${row.name || row.email}`}
                              >
                                See more
                              </button>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, wordBreak: "break-all" }}>{row.email || "—"}</TableCell>
                      <TableCell sx={cellSx}>
                        <StatusBadge status={row.status} />
                      </TableCell>
                      <TableCell sx={cellSx}>{row.role ? <RoleBadge role={row.role} /> : "—"}</TableCell>
                      <TableCell sx={cellSx}>
                        {rowWhen(row)}
                        {row.type === MEMBER_TYPE.INVITE && row.expiresAt && (
                          <span className="block text-xs text-accent-gray-light mt-0.5">Expires {formatOrgDate(row.expiresAt)}</span>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>{renderActions(row)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}
      {!isLoading && visibleRows.length > 0 && (
        <div className="mt-auto">
          <Pager
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setCurrentPage(1);
            }}
          />
        </div>
      )}

      <InviteMemberModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        roles={inviteRoles}
        organisationName={organisationName}
        onInvited={(email) => {
          setNotice(`Invite sent to ${email}.`);
          loadRows();
        }}
      />

      <RolePickerModal
        isOpen={Boolean(roleTarget)}
        onClose={() => setRoleTarget(null)}
        title="Change Role"
        intro={
          roleTarget && (
            <p>
              <span className="font-semibold">{roleTarget.name || roleTarget.email}</span> is currently{" "}
              {roleLabel(roleTarget.role)}. The new role applies the next time they load a page.
            </p>
          )
        }
        roles={roles.filter((role) => role !== ORG_ROLE.ADMIN)}
        initialRole={roleTarget?.role}
        submitLabel="Save Role"
        busyLabel="Saving..."
        onSubmit={async (role) => {
          await changeMemberRole(roleTarget.userId, role);
          setNotice(`${roleTarget.name || roleTarget.email} is now ${roleLabel(role)}.`);
          loadRows();
        }}
      />

      <RolePickerModal
        isOpen={Boolean(approveTarget)}
        onClose={() => setApproveTarget(null)}
        title="Approve Join Request"
        intro={
          approveTarget && (
            <p>
              Choose what <span className="font-semibold">{approveTarget.name || approveTarget.email}</span> can do in{" "}
              {organisationName || "this organisation"}.
            </p>
          )
        }
        roles={roles}
        submitLabel="Approve"
        busyLabel="Approving..."
        onSubmit={async (role) => {
          await approveJoinRequest(approveTarget.inviteId, role);
          setNotice(`${approveTarget.name || approveTarget.email} was approved as ${roleLabel(role)}.`);
          loadRows();
        }}
      />

      <OrgModal
        isOpen={Boolean(noteTarget)}
        onClose={() => setNoteTarget(null)}
        titleId="join-request-note-title"
        title={`Note from ${noteTarget?.name || noteTarget?.email || "this person"}`}
      >
        {noteTarget?.email && <p className="text-sm text-accent-gray-light break-all">{noteTarget.email}</p>}
        <p className="text-sm whitespace-pre-wrap break-words">{noteTarget?.message}</p>
      </OrgModal>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={confirmBusy ? () => {} : () => setConfirm(null)}
        onConfirm={runConfirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        busyLabel={confirm?.busyLabel}
        busy={confirmBusy}
        error={confirmError}
      />
    </div>
  );
};

export default OrganisationMembersPage;
